import React, { Component } from 'react'
import {
  AntennaBottomControl,
  Box,
  Button,
  Card,
  InfoCard,
  Input,
  Page,
  Placeholder,
  Select,
  Spacer,
  Tab,
  TextBox,
  Icons,
  CustomSpacer,
} from 'components'
import AppStore from 'AppStore'
import { __, T } from 'translations/i18n'
import { askUserConfirmation, showToast, sleep } from 'shared/utils'
import { CQTag, CQCounters, CQSerie, Causal, QualitySheet, CQLot, QuantitiesCQDto } from 'api/types'
import RemoteConfig from 'shared/RemoteConfig'
import EncodingsCQ from 'api/EncodingsCQ'
import Sounds from 'shared/Sounds'
import DataDogLogger from 'shared/DataDogLogger'
import QualitySheetModal from 'components/modals/QualitySheetModal'
import QualityControlAgent from './QualityControlAgentNew'
import QualityProvider, { CounterContainer, Counter, TagReadBox, TagReadPlaceholder } from './QualityProvider'
import QualityControlQuantity from '../../components/QualityControlQuantity'
import _ from 'lodash'
import styled from '@emotion/styled'

interface State {
  showQualitySheetModal: boolean
  options: { value: string; label: string; active: boolean }[]
  isReading: boolean
  isVerifing: boolean
  isWriting: boolean
  errorSerie: any
  serie?: CQSerie
  serieCode: string
  counters?: CQCounters
  item?: any
  tag?: CQTag
  warningText?: string
  isSerieSet: boolean
  isActionInProgress: boolean
  causal: Causal[]
  selectedCausal?: Causal
  qualitySheetData?: QualitySheet
  lastSearchedSerieCode?: string
  showScartoModal: boolean
  hideStartReaderButton: boolean
  lots?: CQLot[]
  currentProductQuantity?: number
  hasOperated: boolean
  isVAS: boolean
  hasUnexpectedTag?: boolean
  hasEmptyEncodableQuantity?: boolean
  lastReadedTag?: string
  quantity: Map<string, number>
  serieHasTag?: boolean
  originalQuantity: Map<string, number>
  encodable?: QuantitiesCQDto[]
}

export default class QualityControlWorker extends Component<{}, State> {
  state: State = {
    showQualitySheetModal: false,
    options: [{ value: 'COLLAUDO', label: __(T.labels.testing), active: true }],
    isReading: false,
    isVerifing: false,
    isWriting: false,
    causal: [],
    errorSerie: '',
    isSerieSet: false,
    isActionInProgress: false,
    showScartoModal: false,
    hideStartReaderButton: false,
    hasOperated: false,
    isVAS: false,
    hasUnexpectedTag: false,
    hasEmptyEncodableQuantity: false,
    quantity: new Map<string, number>(),
    serieCode: '',
    originalQuantity: new Map<string, number>(),
  }
  tipoCQ = AppStore.defaultPlace?.attributes?.['tmr.ferragamo.integration.fast.series.tipocq']
  isWSM2 = AppStore.defaultPlace?.attributes?.['tmr.ferragamo.integration.fast.series.wsm2.worker'] === 'S'
  inputRef = React.createRef<HTMLInputElement>()
  operation = RemoteConfig.getOperationConfig<any>('quality-control-worker')
  isPelletteria = (AppStore.defaultPlace?.attributes as any)?.categoryType === 'P'

  clearInputRef = (ref: React.RefObject<HTMLInputElement>) => {
    if (ref && ref.current) {
      ref.current.value = ''
    }
  }

  async componentDidMount() {
    await QualityProvider.commonComponentDidMount(this.stopReader, this.isPelletteria, (causal) =>
      this.setState({ causal })
    )
  }

  async componentWillUnmount() {
    await this.stopReader()
  }

  toggleQualitySheetDetail = () => {
    const { showQualitySheetModal, serie } = this.state
    DataDogLogger.addAction('toggleQualitySheetDetail', {
      serie: serie ?? 'serie is undefined',
      show: !showQualitySheetModal,
    })
    this.setState({ showQualitySheetModal: !showQualitySheetModal })
  }

  changeOptions = async (selectedOption: string) => {
    const { options } = this.state
    options.map((option) => {
      option.active = option.value === selectedOption
      return option
    })
    this.setState({
      options,
      selectedCausal: undefined,
    })
  }

  clearStatus = async () => {
    const { isSerieSet, serieHasTag } = this.state
    await this.stopReader()
    this.clearInputRef(this.inputRef)
    this.setState({
      showQualitySheetModal: false,
      options: isSerieSet
        ? serieHasTag
          ? [
              { value: 'COLLAUDO', label: __(T.labels.testing), active: true },
              { value: 'CANCELLAZIONE', label: __(T.labels.delete), active: false },
            ]
          : [{ value: 'COLLAUDO', label: __(T.labels.testing), active: true }]
        : [],
      isReading: false,
      isVerifing: false,
      isWriting: false,
      errorSerie: '',
      isSerieSet: false,
      isActionInProgress: false,
      qualitySheetData: undefined,
      serie: undefined,
      counters: undefined,
      item: undefined,
      tag: undefined,
      warningText: undefined,
      showScartoModal: false,
      isVAS: false,
      hasUnexpectedTag: false,
      hasEmptyEncodableQuantity: false,
      lastReadedTag: undefined,
    })
  }

  getUpdatedCounters = async (code?: string) => {
    const { serie } = this.state
    const serieCode = code ?? serie!.code
    const counters: CQCounters = await EncodingsCQ.getEncodable(serieCode)
    if (!counters) throw Error(__(T.error.counters_error))
    return counters
  }
  //funzione chiamata quando si cerca una serie
  searchSerie = async (code: string) => {
    await this.getUpdatedSerie(code)
  }

  getUpdatedSerie = async (code: string, startReader = true) => {
    try {
      await this.stopReader()
      code = code.toUpperCase()
      const { serie, options } = this.state
      if (this.isPelletteria && code.length !== 10) throw new Error(__(T.error.serie_code_10_characters))
      if (!this.isPelletteria && code.length !== 14 && code.length !== 17)
        throw new Error(__(T.error.serie_code_14_characters))

      let newSerie = await EncodingsCQ.getCQCustomProductionOrder(code, this.operation)
      if (newSerie.worker.code && newSerie.worker.code !== AppStore.defaultPlace?.code)
        throw new Error(__(T.error.serie_wrong_place))
      if (newSerie.status === 'CLOSED') throw new Error(__(T.error.serie_inspector_check))

      if (!newSerie) {
        console.log('no serie')
        throw new Error(__(T.error.serie_not_found))
      }

      if (serie && newSerie?.code !== serie?.code && serie!.data!.currentLotData![0].items.length !== 0) {
        if (
          !(await askUserConfirmation(
            __(T.messages.serie_change_confirm),
            __(T.messages.serie_close_product_not_associated)
          ))
        ) {
          await this.clearStatus()
          return false
        }
        await this.closeLotto()
      }
      let row
      newSerie.rows.sort((a, b) => a.product.size.value - b.product.size.value)
      if (this.isPelletteria) row = newSerie.rows[0]
      else {
        row = newSerie.rows.find((r) => r.rowCode === code) as any
        // fallback per codici (vecchi) serie 14 caratteri
        if (!row && code.length === 14) {
          const barcodeTo17chars = `${code.slice(0, 4)}000${code.slice(4)}`
          row = newSerie.rows.find((r) => r.rowCode === barcodeTo17chars) as any
        }
      }

      if (!row?.product) {
        console.log('no prodotto', row)
        throw Error(__(T.error.fit_size_not_found))
      }
      let hasUnexpectedTag = false
      let isVAS = false
      const serieHasTag = newSerie.serieHasTag
      DataDogLogger.addAction('searchSerie', { serie: newSerie })

      if (newSerie.availableQuantity === 0 && this.isWSM2 && serieHasTag) {
        showToast({
          title: __(T.misc.warning),
          description: __(T.misc.encoded_items_on_serie),
          status: 'warning',
        })
      }
      if (newSerie && newSerie.customerOrder === '24S1') isVAS = true

      const qualitySheetData = (
        await EncodingsCQ.getQualitySheet(
          newSerie.qualityCode || '',
          newSerie?.worker.code,
          row?.product?.style?.value ?? ''
        )
      )[0]
      const counters = await this.getUpdatedCounters(newSerie.code)
      if (!counters) throw new Error(__(T.error.counters_error))
      let hasEmptyEncodableQuantity = false
      if (counters.encodableCounts.quantity <= 0) hasEmptyEncodableQuantity = true
      let currentLotData = newSerie.lots?.filter((lot) => lot.status === 'INSPECTED_1')[0]
      if (counters.encodableCounts.quantity === 0) {
        if (!(currentLotData && currentLotData.code)) {
          showToast({
            title: __(T.misc.warning),
            description: __(T.misc.no_lot_open_all_item_encoded),
            status: 'warning',
          })
          return await this.clearStatus()
        }
        if (currentLotData && currentLotData.items!.length > 0 && this.state.hasOperated) {
          await EncodingsCQ.changeStatusLottoToReadyInspection(currentLotData.id)
          showToast({
            title: __(T.misc.warning),
            description: __(T.misc.lot_closed_cannot_encode_items),
            status: 'warning',
          })
          return this.clearStatus()
        }
        showToast({
          title: __(T.misc.warning),
          description: __(T.misc.cannot_encode_items_check_wsm2),
          status: 'warning',
        })
        startReader = false
      }
      const newSerieFormatted: CQSerie = {
        id: newSerie.id,
        code: newSerie.code,
        productId: row.product.id,
        data: {
          values: {
            'Tag/Cal': `${row.product.size.value}/${row.product.fit.value}`,
            Modello: row.product.style.value,
            Variante: row.product.variant.value,
            Stagione: row.product.season.slice(0, 2) + row.product.season.slice(4),
            Lavorante: newSerie.worker.description ?? 'aa',
            'Pezzi totali serie': counters.itemStatusCounts.seriesSize,
          },
          additional: {
            workerSyncCode: newSerie.worker.syncCode,
            productSku: row.product.sku,
            productSize: row.product.size.value,
            productCode: row.product.code,
            customerOrder: newSerie.customerOrder?.trim() ?? '',
            nationCode: newSerie.worker.countryCode ?? '',
          },
          descriptions: {
            Modello: row.product.style.valueDescription,
            Taglia: row.product.size.valueDescription,
            Calzata: row.product.fit.valueDescription,
            Variante: row.product.variant.valueDescription,
          },
          currentLotData: [currentLotData!],
        },
      }
      const lots = newSerie.lots?.filter((lot) => lot.status !== 'INSPECTED_1')
      let encodable: QuantitiesCQDto[] = []
      if (this.isPelletteria) {
        const lotCode = newSerieFormatted?.data.currentLotData?.[0]?.code ?? ''
        encodable = [
          {
            rowCode: newSerie.code,
            quantity:
              counters.encodableCounts.quantity === 0 && this.isWSM2
                ? 0
                : newSerieFormatted.data.values['Pezzi totali serie'] - QualityProvider.getEncodati(lotCode, counters),
          },
        ]
      } else {
        const rows = counters.encodableCounts.rows
        rows.forEach((row) => {
          const rowCode =
            newSerie.rows.filter((r) => r.product.fit.value === row.fit && r.product.size.value === row.size)[0]
              .rowCode ?? ''
          if (row.quantity > 0) {
            encodable.push({
              rowCode: rowCode,
              quantity: row.quantity,
              fit: row.fit,
              size: row.size,
            })
          }
        })
      }
      if (this.getMaxQuantity(encodable) === 0 && !currentLotData) {
        showToast({
          title: __(T.misc.warning),
          description: __(T.misc.no_lot_open_all_item_encoded),
          status: 'warning',
        })
        return await this.clearStatus()
      }
      const quantity = this.getQuantityToAdvance(newSerie.code, encodable) ?? new Map<string, number>()
      const currentProductCounters =
        counters.encodableCounts.rows &&
        counters.encodableCounts.rows.filter(function (v) {
          return v.fit === row.product.fit.value && v.size === row.product.size.value
        })[0]
      if (currentProductCounters && currentProductCounters.quantity <= 0 && serieHasTag) {
        showToast({
          title: __(T.misc.warning),
          description: __(T.misc.items_limit_reached_wsm2),
          status: 'warning',
        })
      }
      this.setState({
        serie: newSerieFormatted,
        errorSerie: undefined,
        warningText: !serieHasTag ? __(T.misc.no_tag) : undefined,
        isSerieSet: true,
        counters,
        qualitySheetData,
        lastSearchedSerieCode: code,
        hideStartReaderButton: !serieHasTag,
        lots,
        currentProductQuantity: currentProductCounters ? currentProductCounters.quantity : 0,
        isVAS,
        hasUnexpectedTag,
        hasEmptyEncodableQuantity,
        serieHasTag,
        quantity,
        originalQuantity: _.cloneDeep(quantity),
        options: serieHasTag
          ? [
              { value: 'COLLAUDO', label: __(T.labels.testing), active: true },
              { value: 'CANCELLAZIONE', label: __(T.labels.delete), active: false },
            ]
          : [{ value: 'COLLAUDO', label: __(T.labels.testing), active: true }],
        serieCode: newSerie.code,
        encodable,
      })
      const totalItems = (newSerie.lots ?? []).reduce(
        (sum, lot) => sum + (lot.items?.filter((i) => i.status === 'CQ' || i.status === 'CQ2').length ?? 0),
        0
      )
      if (totalItems === newSerie.requestedQuantity) return await this.closeLotto()
      Sounds.tap()
      const mode = options?.find((opt) => opt?.active === true)?.value
      // in scarto non faccio partire il reader automaticamente perchè va prima selezionata la causale di scarto
      if (
        mode === 'COLLAUDO' &&
        newSerieFormatted &&
        counters.encodableCounts.quantity !== 0 &&
        serieHasTag &&
        startReader
      )
        await this.startReader()
      this.clearInputRef(this.inputRef)
      return true
    } catch (error) {
      DataDogLogger.addError(error, { type: 'searchSerie' })
      showToast({
        title: __(T.error.error),
        description: (error as Error).message ?? __(T.error.serie_error),
        status: 'error',
      })
      await this.clearStatus()
      return false
    }
  }

  getQuantityToAdvance = (serieCode: string, encodable: QuantitiesCQDto[]) => {
    const quantityToAdvance = new Map<string, number>()
    if (this.isPelletteria) {
      quantityToAdvance.set(serieCode, this.getMaxQuantity(encodable))
    } else {
      encodable.forEach((obj) => {
        const quantity = obj.quantity
        if (quantity > 0) {
          quantityToAdvance.set(obj.rowCode, quantity)
        }
      })
    }
    return quantityToAdvance
  }

  startReader = async () => {
    const { options, selectedCausal } = this.state
    await QualityProvider.startReader(
      (isReading) => this.setState({ isReading }),
      (tag) => this.setState({ tag }),
      (tag) => this.verifyReadedTag(tag),
      options,
      selectedCausal
    )
  }

  verifyReadedTag = async (tag: CQTag) => {
    const { options, serie, currentProductQuantity, lastReadedTag, serieHasTag } = this.state
    const mode = options?.find((opt) => opt?.active === true)?.value
    try {
      if (lastReadedTag && lastReadedTag === tag.uid && mode === 'COLLAUDO') return this.startReader()
      if (
        mode === 'COLLAUDO' &&
        currentProductQuantity &&
        currentProductQuantity <= 0 &&
        !this.isPelletteria &&
        this.tipoCQ === 'CENTO' &&
        serieHasTag
      )
        throw Error(__(T.misc.items_limit_reached_wsm2))
      this.setState({
        isReading: false,
        isVerifing: true,
      })
      const res = await EncodingsCQ.verifyCQWorker({
        productionOrderId: serie!.id,
        productId: serie!.productId,
        identifiers: [{ code: tag?.uid, type: 'NFCTag' }] as any,
        configurationId: this.operation.id,
        operationType: mode,
      })
      if (res.errors.length > 0) {
        QualityProvider.showErrorToast(res)
        if (res.errors[0].errorCode) {
          throw Error(
            T.error[res.errors[0].errorCode.replaceAll('.', '_')]
              ? __(T.error[res.errors[0].errorCode.replaceAll('.', '_')])
              : res.errors[0].errorCode.replaceAll('.', '_')
          )
        }
      }
      if (
        res.warnings.length > 0 &&
        !(await askUserConfirmation(__(T.misc.warning_message_in_serie), __(T.misc.proceed)))
      )
        return this.setState({ isVerifing: false })
      this.setState({
        item: res.item,
        tag,
      })
      if (mode === 'SCARTO') return this.setState({ showScartoModal: true })
      this.setState({ isVerifing: false })
      if (
        mode === 'COLLAUDO' &&
        (!res.item ||
          res.item.status !== 'SC' ||
          (res.item.status === 'SC' &&
            (await askUserConfirmation(
              __(T.misc.warning),
              __(T.misc.item_discarded_with_causal_proceed, { causal: res.item?.causal?.description ?? '' })
            ))))
      ) {
        await this.collaudoItem()
      }
      if (mode === 'CANCELLAZIONE') {
        if (!res.item) throw Error(__(T.error.cannot_delete_no_item_encoded))
        await this.cancellaItem()
      }
    } catch (error) {
      this.setState({
        isReading: false,
        isVerifing: false,
      })
      showToast({
        title: __(T.error.error),
        description: (error as Error).message ?? __(T.error.WEBRESOURCE_GENERIC_ERROR),
        status: 'error',
      })
    }
  }

  collaudoItem = async () => {
    const { tag, serie, counters, lastSearchedSerieCode } = this.state
    const additionalSerieData = serie?.data.additional
    try {
      DataDogLogger.addAction('processAssociate', { serie, tag })
      this.setState({ isWriting: true })
      if (
        counters!.itemStatusCounts.seriesSize - counters!.itemStatusCounts.tested <= 0 ||
        counters!.encodableCounts.quantity <= 0
      )
        throw Error(__(T.error.serie_limit_associate_error))
      await QualityProvider.collaudaItemEmulazione(additionalSerieData, tag, serie)
      const response = await EncodingsCQ.associateLot({
        productionOrderId: serie?.id,
        productId: serie!.productId,
        identifiers: [{ code: tag?.uid, type: 'NFCTag' }] as any,
        zoneId: AppStore.defaultZone?.id,
        configurationId: this.operation.id,
        scannedBarCode: lastSearchedSerieCode,
      })
      if (!response.success) {
        console.log(response.error)
        const errorFormatted =
          response.errors && T.error[response.errors[0].errorCode.replaceAll('.', '_')]
            ? __(T.error[response.errors[0].errorCode.replaceAll('.', '_')])
            : __(T.error.product_association_error)
        throw Error(errorFormatted)
      }
      this.setState({
        isWriting: false,
        isReading: false,
        hasOperated: true,
        lastReadedTag: tag?.uid,
      })
      Sounds.success()
      showToast({
        title: __(T.misc.success),
        description: __(T.messages.product_associated_correctly),
        status: 'success',
      })
      await this.getUpdatedSerie(lastSearchedSerieCode ?? '', this.isPelletteria)
    } catch (error) {
      DataDogLogger.addError(error, { type: 'processAssociate' })
      this.setState({ isWriting: false })
      showToast({
        title: __(T.error.error),
        description: (error as Error).message ?? __(T.error.WEBRESOURCE_GENERIC_ERROR),
        status: 'error',
      })
    }
  }

  scartoItem = async (reversible = true) => {
    const { tag, serie, selectedCausal, lastSearchedSerieCode } = this.state
    try {
      DataDogLogger.addAction('processDrop', { serie, tag, selectedCausal })
      this.setState({ showScartoModal: false, isReading: false, isWriting: true })
      if (!AppStore.emulation) {
        const res = await QualityControlAgent.writeTagSC(
          AppStore.loggedUser?.username as string,
          tag?.uid as string,
          AppStore.defaultPostazione ?? '',
          selectedCausal?.id as string
        )
        if (res?.status !== '0') throw Error(__(T.error.writing_tag_error))
      }
      const response = await EncodingsCQ.dropLotWorker({
        productionOrderId: serie!.id,
        productId: serie!.productId,
        identifiers: [{ code: tag?.uid, type: 'NFCTag' }] as any,
        zoneId: AppStore.defaultZone?.id,
        configurationId: this.operation.id,
        causalId: selectedCausal?.id,
        reversible,
      })
      if (!response.success) throw Error(__(T.error.product_drop_error))
      Sounds.success()
      showToast({
        title: __(T.misc.success),
        description: __(T.messages.product_dropped_correctly),
        status: 'success',
      })
      this.setState({
        errorSerie: undefined,
        isWriting: false,
        isReading: false,
      })
      await this.changeOptions('COLLAUDO')
      await this.getUpdatedSerie(lastSearchedSerieCode ?? '', false)
    } catch (error) {
      DataDogLogger.addError(error, { type: 'processDrop' })
      this.setState({ isWriting: false })
      showToast({
        title: __(T.error.error),
        description: (error as Error).message ?? __(T.error.WEBRESOURCE_GENERIC_ERROR),
        status: 'error',
      })
    }
  }

  cancellaItem = async () => {
    try {
      const { tag, serie, item, lastSearchedSerieCode } = this.state
      if (await QualityProvider.checkStatusCancella(item.status)) {
        await QualityProvider.cancellaItemEmulazione((isWriting) => this.setState({ isWriting }), tag, serie, item)
        await EncodingsCQ.deleteLotWorker({
          productionOrderId: item?.productionOrderRow?.order?.id ?? serie?.id,
          productId: item?.product?.id as string,
          identifiers: [{ code: tag?.uid, type: 'NFCTag' }] as any,
          zoneId: AppStore.defaultZone?.id,
          configurationId: this.operation.id,
        })
        await QualityProvider.cancellaItem(
          (errorSerie: any, isWriting: boolean, isReading: boolean) =>
            this.setState({ errorSerie, isWriting, isReading }),
          (option) => this.changeOptions(option),
          (lastCode) => this.getUpdatedSerie(lastCode, false),
          lastSearchedSerieCode
        )
      }
    } catch (error) {
      DataDogLogger.addError(error, { type: 'processDelete' })
      this.setState({ isWriting: false })
      showToast({
        title: __(T.error.error),
        description: (error as Error).message ?? __(T.error.WEBRESOURCE_GENERIC_ERROR),
        status: 'error',
      })
    }
  }

  stopReader = async () => {
    await QualityControlAgent.stopReader()
    await sleep(700)
    this.setState({
      isReading: false,
    })
  }

  //funzione che chiude il lotto
  closeLotto = async () => {
    try {
      const { serieHasTag, serie, lastSearchedSerieCode, quantity, serieCode } = this.state
      DataDogLogger.addAction('closeSerie', { serie: serie })
      if (
        !serie ||
        !serie.data ||
        !serie.data.currentLotData ||
        serie.data.currentLotData.length === 0 ||
        !serie.data.currentLotData[0]
      )
        return
      this.setState({ isActionInProgress: true }) //settings action in progress for disable buttons click
      if (this.tipoCQ === 'CAMP_LOTTI' && !serieHasTag) {
        if (this.isPelletteria) {
          if (quantity.get(serieCode) ?? 0 > 0)
            await EncodingsCQ.confirmWorkerLottiNoTag(
              serie!.data!.currentLotData![0].id,
              serie!.productId,
              quantity.get(serieCode) ?? 0
            )
          else throw Error(__(T.error.no_items_encoded_cant_advance_lot))
        } else {
          let somma = 0
          quantity.forEach(async (quantity) => (somma += quantity))
          if (somma > 0)
            await EncodingsCQ.confirmWorkerLottiNoTagCalzature(serie!.data!.currentLotData![0].id, quantity)
          else throw Error(__(T.error.no_items_encoded_cant_advance_lot))
        }
        await this.clearStatus()
        showToast({
          title: __(T.misc.success),
          description: __(T.messages.lot_closed),
          status: 'success',
        })
        this.setState({ isActionInProgress: false, quantity: new Map<string, number>() })
        return this.searchSerie(lastSearchedSerieCode ?? '')
      } else {
        if (serie!.data!.currentLotData![0].items.length === 0)
          throw Error(__(T.error.encode_one_item_before_close_lot))
        const res = await EncodingsCQ.changeStatusLottoToReadyInspection(serie!.data!.currentLotData![0].id)
        if (res && res.errors.length > 0)
          throw Error(T.error[res.errors[0].errorCode] ? __(T.error[res.errors[0].errorCode]) : res.errors[0].errorCode)
        await this.clearStatus()
        showToast({
          title: __(T.misc.success),
          description: __(T.messages.lot_closed),
          status: 'success',
        })
        return this.setState({ isActionInProgress: false, quantity: new Map<string, number>() })
      }
    } catch (error) {
      DataDogLogger.addError(error, { type: 'closeSerie' })
      showToast({
        sound: false,
        title: __(T.error.error),
        description: (error as Error).message,
        status: 'error',
      })
      return this.setState({ isActionInProgress: false })
    }
  }

  renderHeader = () => {
    const { options, isReading, isVerifing, isWriting, qualitySheetData, isSerieSet } = this.state
    const showAntennaReadingIcon = isReading || isWriting || isVerifing
    return (
      <>
        {qualitySheetData && (
          <Button variant="secondary" style={{ minHeight: 55 }} onClick={this.toggleQualitySheetDetail}>
            {__(T.misc.quality_sheet)}
          </Button>
        )}
        <Spacer />
        {isSerieSet && (
          <Tab options={options} onOptionSelected={this.changeOptions} disabled={showAntennaReadingIcon} />
        )}
      </>
    )
  }

  renderBottomBar = () => {
    const { isReading, isVerifing, isWriting, hideStartReaderButton, options, hasEmptyEncodableQuantity } = this.state
    const showAntennaReadingIcon = isReading || isWriting || isVerifing
    const mode = options?.find((opt) => opt?.active === true)?.value
    const disableButton = mode === 'COLLAUDO' && hasEmptyEncodableQuantity
    return (
      <>
        {showAntennaReadingIcon && (
          <Box row>
            <Box flex>
              <AntennaBottomControl />
            </Box>
            <Spacer />
            <Button
              title={__(T.placeholder.stop_reader)}
              onClick={this.stopReader}
              disabled={isWriting || isVerifing}
            />
          </Box>
        )}
        {!showAntennaReadingIcon && !hideStartReaderButton && (
          <Button title={__(T.placeholder.start_reader)} onClick={this.startReader} disabled={disableButton} />
        )}
      </>
    )
  }

  renderScartoModal = () => {
    return (
      <Box>
        <TextBox text={__(T.messages.select_discard_type)} type="error" />
        <Box row mt={15} center>
          <Button
            variant="default"
            title={__(T.misc.cancel)}
            onClick={() => {
              return this.setState({ showScartoModal: false })
            }}
          />
          <Spacer />
          <Button title={__(T.placeholder.reversible)} onClick={this.scartoItem} />
          <Spacer />
          <Button title={__(T.placeholder.irreversible)} onClick={() => this.scartoItem(false)} />
        </Box>
      </Box>
    )
  }

  renderInfoSerie = () => {
    const { serie, lots, counters } = this.state
    if (!serie) return
    return (
      <>
        <InfoCard item={serie!.data.values} title={__(T.misc.serie)} subtitle={serie!.code} />
        <CustomSpacer />
        <CounterContainer
          onClick={() => {
            QualityProvider.showLotsModal(lots, serie, counters).then((r) => r)
          }}
        >
          <Card title={__(T.labels.lot)} nomargin style={{ minWidth: 125, padding: 5, paddingLeft: 10 }}>
            <Box style={{ flexDirection: 'row', justifyContent: 'space-between' }}>
              <Counter>
                {(serie.data.currentLotData![0] && serie.data.currentLotData![0].code) ?? __(T.misc.no_open_lot)}
              </Counter>
              <Icons.Info style={{ alignSelf: 'center', marginTop: -20 }} width={25} height={25} />
            </Box>
          </Card>
        </CounterContainer>
      </>
    )
  }
  renderSideBar = () => {
    const { errorSerie, isReading, isWriting, isVerifing, warningText, options, isVAS, isSerieSet } = this.state
    const isDisabled = isReading || isWriting || isVerifing
    const selectedOption = options.find((opt) => opt.active)?.value
    return (
      <>
        <Page.Sidebar style={{ overflowY: 'auto', scrollbarWidth: 'none' }}>
          <Input
            registerClear={(cb) => (this.clearInputRef = cb)}
            inputRef={this.inputRef}
            autoFocus={this.inputRef?.current?.value === '' && selectedOption !== 'SCARTO' && !isSerieSet}
            onChange={() => this.forceUpdate()}
            barcode
            error={errorSerie}
            placeholder={__(T.placeholder.serie_barcode)}
            onEnter={this.searchSerie}
            disabled={isDisabled}
            focusOptions={{ preventScroll: true }}
          />
          <CustomSpacer />
          {warningText && (
            <>
              <WarningText text={warningText} type="warning" p={2} />
              <CustomSpacer />
            </>
          )}
          {isVAS && (
            <>
              <WarningText text={__(T.messages.vas_serie)} type="warning" p={2} />
              <CustomSpacer />
            </>
          )}
          {this.renderInfoSerie()}
        </Page.Sidebar>
      </>
    )
  }

  renderIsReadingBox = () => {
    return (
      <Box flex center>
        <Placeholder style={{ width: 370 }}>{__(T.messages.near_product_to_reader)}</Placeholder>
      </Box>
    )
  }

  renderIsVerifingBox = () => {
    return (
      <TagReadBox bgColor="#18a0c2">
        <TagReadPlaceholder color="#fff" style={{ width: 370 }}>
          {__(T.misc.checking_dont_move)}
        </TagReadPlaceholder>
      </TagReadBox>
    )
  }

  renderIsWritingBox = () => {
    return (
      <TagReadBox bgColor="#18a0c2">
        <TagReadPlaceholder color="#fff" style={{ width: 370 }}>
          {__(T.misc.writing_dont_move)}
        </TagReadPlaceholder>
      </TagReadBox>
    )
  }

  renderScartoSelect = () => {
    const { selectedCausal, causal, serie } = this.state
    return (
      <Box flex style={{ width: '100%' }}>
        <Select
          onSelect={(causalSel) => {
            DataDogLogger.addAction('Causal selection', { serie, causal })
            this.setState({ selectedCausal: causalSel })
          }}
          placeholder={__(T.placeholder.drop_causal)}
          options={causal}
          config={{ value: 'id', label: 'fastId', secondaryLabel: 'description' }}
          isClearable={false}
          defaultValue={selectedCausal}
          multiLabel
        />
      </Box>
    )
  }

  renderQualitySheetModal = () => {
    const { showQualitySheetModal, serie, qualitySheetData } = this.state
    return (
      <>
        {showQualitySheetModal && (
          <QualitySheetModal
            serie={serie}
            qualitySheet={qualitySheetData}
            onClose={this.toggleQualitySheetDetail}
            visible={this.state.showQualitySheetModal}
          />
        )}
      </>
    )
  }

  emulateTag = (tags: string[]) => {
    const tagRead = {
      errorDesc: '',
      status: '0',
      uid: tags[0],
      uuid: '045442D2B16C80045442D2B16C80',
    }
    this.verifyReadedTag(tagRead).then((r) => r)
  }

  renderInsertQuantity = () => {
    const { quantity, serieCode, encodable } = this.state
    if (this.isPelletteria) {
      let somma = 0
      quantity.forEach(async (quantity) => (somma += quantity))
      if (somma === 0) return
      return (
        <QualityControlQuantity
          quantityToAdvance={quantity.get(serieCode) ?? 0}
          checkMaxQuantity={() => this.checkMaxQuantityToAdvance(serieCode)}
          onDecrementQuantity={() => this.onDecrementQuantity(serieCode)}
          onIncrementQuantity={() => this.onIncrementQuantity(serieCode)}
          enableIcons
        />
      )
    } else {
      return (
        <Box flex style={{ overflowY: 'auto' }}>
          {Array.from(quantity.entries()).map(([key, value]) => (
            <React.Fragment key={key}>
              <Counter>
                {key} ({encodable?.find((obj) => obj.rowCode === key)!.size}/
                {encodable?.find((obj) => obj.rowCode === key)!.fit})
              </Counter>
              <QualityControlQuantity
                quantityToAdvance={value}
                checkMaxQuantity={() => this.checkMaxQuantityToAdvance(key)}
                onDecrementQuantity={() => this.onDecrementQuantity(key)}
                onIncrementQuantity={() => this.onIncrementQuantity(key)}
                enableIcons
              />
              <CustomSpacer />
            </React.Fragment>
          ))}
        </Box>
      )
    }
  }

  checkMaxQuantityToAdvance = (rowCode: string) => {
    const { quantity, encodable } = this.state
    if (this.isPelletteria) {
      return (quantity.get(rowCode) ?? 0) >= (encodable ? encodable[0].quantity : 0)
    } else {
      const { quantity, originalQuantity } = this.state
      return (quantity.get(rowCode) ?? 0) >= (originalQuantity.get(rowCode) ?? 0)
    }
  }

  onDecrementQuantity = (rowCode: string) => {
    const { quantity } = this.state
    quantity.set(rowCode, (quantity.get(rowCode) ?? 0) - 1)
    this.setState({ quantity })
  }

  onIncrementQuantity = (rowCode: string) => {
    const { quantity } = this.state
    quantity.set(rowCode, (quantity.get(rowCode) ?? 0) + 1)
    this.setState({ quantity })
  }

  getMaxQuantity = (encodable?: QuantitiesCQDto[]) => {
    if (!encodable) return 0
    let somma = 0
    encodable.forEach((obj) => {
      somma += obj.quantity
    })
    return somma
  }

  renderCounter = () => {
    const { counters, encodable, quantity, serieHasTag } = this.state
    const toBeTested =
      Math.ceil(
        (Array.from(quantity.values()).reduce((acc, value) => acc + value, 0) / 100) *
          Number(AppStore.defaultPlace?.attributes?.['tmr.ferragamo.cq.inspector.totest'] ?? '0')
      ) ?? 0
    const somma = this.getMaxQuantity(encodable)
    const lotCounts = counters!.itemStatusCounts.lotCounts.filter((lot) => lot.lotStatus === 'INSPECTED_1')[0] ?? {
      tested: 0,
      discarded: 0,
      toBeTested: 0,
    }

    return (
      <CounterContainer flex>
        <CounterContainer row flex>
          <Card title={__(T.misc.encodable_items)} nomargin style={{ padding: 5, paddingLeft: 10, flex: 1 }}>
            <Counter>{somma === 0 ? '-' : somma}</Counter>
          </Card>
          <CustomSpacer />
          <Card title={__(T.placeholder.tested)} nomargin style={{ padding: 5, paddingLeft: 10, flex: 1 }}>
            <Counter>{!lotCounts || (lotCounts.tested ?? 0) === 0 ? '-' : lotCounts.tested}</Counter>
          </Card>
        </CounterContainer>
        <CustomSpacer />
        <CounterContainer row>
          <Card title={__(T.placeholder.dropped)} nomargin style={{ padding: 5, paddingLeft: 10, flex: 1 }}>
            <Counter>{!lotCounts || (lotCounts.discarded ?? 0) === 0 ? '-' : lotCounts.discarded}</Counter>
          </Card>
          <CustomSpacer />
          <Card title={__(T.misc.to_be_tested)} nomargin style={{ padding: 5, paddingLeft: 10, flex: 1 }}>
            {serieHasTag && (
              <Counter>{lotCounts && (lotCounts.toBeTested ?? 0) === 0 ? '-' : lotCounts.toBeTested}</Counter>
            )}
            {!serieHasTag && <Counter>{toBeTested === 0 ? '-' : toBeTested}</Counter>}
          </Card>
        </CounterContainer>
      </CounterContainer>
    )
  }

  render() {
    const { isReading, isVerifing, isWriting, isSerieSet, options, showScartoModal, isActionInProgress, serieHasTag } =
      this.state
    const disableButton = isReading || isWriting || isVerifing
    const mode = options.find((opt) => opt.active)?.value
    return (
      <Page
        title={__(T.titles.quality_control)}
        enableEmulation
        headerRight={this.renderHeader()}
        emulationFunction={this.emulateTag}
      >
        {this.renderSideBar()}
        <Page.Content>
          {isSerieSet && (
            <>
              <CounterContainer row>
                {this.renderCounter()}
                <CustomSpacer />
                <CounterContainer flex>
                  <Button
                    style={{ flex: 'auto' }}
                    title={__(T.misc.close_lot)}
                    loading={isActionInProgress}
                    variant="primary"
                    onClick={this.closeLotto}
                    disabled={disableButton}
                  />
                </CounterContainer>
              </CounterContainer>
              <CustomSpacer />
              {!serieHasTag && (
                <>
                  {this.renderInsertQuantity()}
                  <CustomSpacer />
                </>
              )}
            </>
          )}
          {serieHasTag && isSerieSet && !showScartoModal && (
            <>
              {mode === 'SCARTO' && this.renderScartoSelect()}
              <Box flex hcenter vcenter>
                {isReading && this.renderIsReadingBox()}
                {isVerifing && this.renderIsVerifingBox()}
                {isWriting && this.renderIsWritingBox()}
              </Box>
              <CustomSpacer />
              {this.renderBottomBar()}
            </>
          )}
          {showScartoModal && this.renderScartoModal()}
        </Page.Content>
        {this.renderQualitySheetModal()}
      </Page>
    )
  }
}

const WarningText = styled(TextBox)`
  font-size: 15px;
`
